import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuShieldCheck } from 'react-icons/lu';
import AdminAuthShell from '../components/AdminAuthShell';
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  clearOtpSession,
  extendOtpSession,
  formatCountdown,
  getOtpSecondsRemaining,
  getOtpSession,
} from '../utils/otpSession';

const OTP_LENGTH = 6;
const DEFAULT_OTP_SECONDS = 300;

const AdminOtpVerify = () => {
  const [otpCode, setOtpCode] = useState('');
  const [session, setSession] = useState(() => getOtpSession());
  const [secondsLeft, setSecondsLeft] = useState(() => getOtpSecondsRemaining(getOtpSession()));
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const otpInputRef = useRef(null);
  const { verifyOtp, resendOtp } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!session?.otpSessionId) {
      navigate('/admin/login', { replace: true });
      return;
    }

    otpInputRef.current?.focus();
  }, [session, navigate]);

  useEffect(() => {
    if (!session?.expiresAt) return undefined;

    const tick = () => {
      setSecondsLeft(getOtpSecondsRemaining(getOtpSession()));
    };

    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [session?.expiresAt, session?.otpSessionId]);

  const isExpired = secondsLeft <= 0;

  const handleVerify = async (event) => {
    event.preventDefault();

    if (isExpired) {
      setError('Verification code expired. Please sign in again.');
      return;
    }

    if (!session?.otpSessionId) {
      navigate('/admin/login', { replace: true });
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');

    try {
      await verifyOtp(session.otpSessionId, otpCode);
      clearOtpSession();
      navigate(session.redirectTo || '/admin', { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!session?.otpSessionId) {
      navigate('/admin/login', { replace: true });
      return;
    }

    setResending(true);
    setError('');
    setInfo('');

    try {
      const response = await resendOtp(session.otpSessionId);
      const expiresInSeconds =
        response?.otpExpiresInSeconds ?? response?.OtpExpiresInSeconds ?? DEFAULT_OTP_SECONDS;
      const maskedEmail = response?.maskedEmail ?? response?.MaskedEmail ?? session.maskedEmail;
      extendOtpSession(expiresInSeconds, maskedEmail);
      const nextSession = getOtpSession();
      setSession(nextSession);
      setSecondsLeft(getOtpSecondsRemaining(nextSession));
      setOtpCode('');
      setInfo(`A new verification code was sent to ${maskedEmail || 'your email'}.`);
      otpInputRef.current?.focus();
    } catch (err) {
      setError(err.message || 'Could not resend verification code.');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    clearOtpSession();
    navigate(isExpired ? '/admin/login?otpExpired=1' : '/admin/login', { replace: true });
  };

  if (!session?.otpSessionId) {
    return null;
  }

  return (
    <AdminAuthShell>
      <div className="mb-8 flex flex-col items-center text-center">
        <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/15 text-primary">
          <LuShieldCheck size={30} />
        </div>
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Verify OTP</h1>
        <div className="mt-3 h-1 w-12 rounded-full bg-primary" />
        <p className="mt-4 text-sm leading-relaxed text-slate-500 dark:text-gray-400">
          Enter the 6-digit code sent to{' '}
          <span className="font-semibold text-slate-700 dark:text-gray-200">{session.maskedEmail}</span>
        </p>
      </div>

      <form className="space-y-5" onSubmit={handleVerify}>
        <div>
          <label className="mb-2 block text-sm font-bold text-slate-900 dark:text-white">OTP Code</label>
          <div className="relative">
            <input
              ref={otpInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
              placeholder="123456"
              disabled={isExpired}
              className="w-full rounded-xl border border-primary/25 bg-white py-3.5 text-center text-2xl tracking-[0.35em] text-slate-900 placeholder:text-base placeholder:tracking-normal placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-primary/30 dark:bg-[#1a1a1a] dark:text-white dark:placeholder:text-gray-500"
              required
            />
          </div>
        </div>

        <div
          className={`rounded-xl border px-4 py-3 text-center text-sm font-semibold ${
            isExpired
              ? 'border-red-300/60 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200'
              : 'border-primary/25 bg-primary/5 text-slate-700 dark:border-primary/30 dark:bg-primary/10 dark:text-gray-200'
          }`}
        >
          {isExpired ? 'Code expired' : `Expires in ${formatCountdown(secondsLeft)}`}
        </div>

        {info ? (
          <p className="rounded-xl border border-emerald-300/60 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
            {info}
          </p>
        ) : null}

        {error ? <p className="text-sm text-red-500 dark:text-red-400">{error}</p> : null}

        <button
          type="submit"
          disabled={loading || isExpired || otpCode.length < OTP_LENGTH}
          className="w-full rounded-full bg-primary px-6 py-3.5 text-sm font-extrabold text-black shadow-[0_10px_30px_rgba(243,199,21,0.35)] transition-transform hover:scale-[1.02] disabled:opacity-70"
        >
          {loading ? 'Verifying...' : 'Verify & Sign In →'}
        </button>

        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="w-full text-sm font-semibold text-slate-500 transition-colors hover:text-primary disabled:opacity-70 dark:text-gray-400"
        >
          {resending ? 'Resending code...' : 'Resend OTP Code'}
        </button>

        <button
          type="button"
          onClick={handleBackToLogin}
          className="w-full text-sm font-semibold text-slate-500 transition-colors hover:text-primary dark:text-gray-400"
        >
          Back to Sign In
        </button>
      </form>

      <p className="mt-8 text-center text-sm text-slate-500 dark:text-gray-400">
        Back to website?{' '}
        <Link to="/" className="font-bold text-primary hover:underline">
          Visit Home
        </Link>
      </p>
    </AdminAuthShell>
  );
};

export default AdminOtpVerify;
